import Matercolor from 'matercolors';
import { Theme } from '@mui/material';

import {
  getBeginningOfMonth,
  getCurrentMonth,
  getEndOfMonth,
  formatDateToApiFormat
} from '../../../../../../constants/helpers/dates';
import { UrlParam } from '../../../../../../api/interfaces';
import { FILTERS } from '../../../../../common/filters/constants';
import { PAGE_TITLE } from '../../../consts';

import { NO_DATA } from './const';
import { ServicesCostData, ChartData, Service, ServiceValue } from './interface';

export const generateYearsHistory = (yearsRange: number, dateProvider = getCurrentMonth()): string[] => {
  let [year, month] = dateProvider.split('-').map(value => parseInt(value, 10));

  const monthsRange = yearsRange * 12;
  const dates = [];

  for (let i = 0; i <= monthsRange; i += 1) {
    dates.push(`${year}-${month.toString().padStart(2, '0')}`);

    if (month === 1) {
      year -= 1;
      month = 12;
    } else {
      month -= 1;
    }
  }

  return dates;
};

// checks for null OR undefine
const hasValue = (serviceValue: ServiceValue): serviceValue is { value: number } => serviceValue != null;

const serviceNotIncludedInPieChart = (service: ServiceValue): boolean => !hasValue(service) || service.value === 0;

export const prepareChartData = (servicesCostData: ServicesCostData | undefined, theme: Theme): ChartData[] => {
  const generatedChartData: ChartData[] = [];

  if (servicesCostData && servicesCostData.services) {
    const {
      airtime,
      transfer,
      ussd,
      wap,
      voice,
      payout,
      sms,
      operatorLookup,
      onDemandPaymentAndAcquisition,
      merchantPayment,
      customerValidation,
      customerAuthentication,
      customerEnrollment
    } = servicesCostData.services;
    const accessPallete = new Matercolor('#47B84E');
    const valuePallete = new Matercolor('#4E47B8');
    const identityPallete = new Matercolor('#B84E47');
    const emptyChart = Object.keys(servicesCostData.services).every((key: Service) =>
      serviceNotIncludedInPieChart(servicesCostData.services[key])
    );

    if (emptyChart) {
      generatedChartData.push({
        name: NO_DATA,
        value: 1,
        color: theme.customPalette.silverSand
      });
    }

    // Access

    if (hasValue(ussd)) {
      generatedChartData.push({
        name: PAGE_TITLE.USSD,
        value: ussd.value,
        color: accessPallete[100]
      });
    }

    if (hasValue(wap)) {
      generatedChartData.push({
        name: PAGE_TITLE.WAP,
        value: wap.value,
        color: accessPallete[200]
      });
    }

    if (hasValue(voice)) {
      generatedChartData.push({
        name: PAGE_TITLE.VOICE,
        value: voice.value,
        color: accessPallete[900]
      });
    }

    if (hasValue(sms)) {
      generatedChartData.push({
        name: PAGE_TITLE.SMS,
        value: sms.value,
        color: accessPallete[300]
      });
    }

    if (hasValue(operatorLookup)) {
      generatedChartData.push({
        name: PAGE_TITLE.OPERATOR_LOOKUP,
        value: operatorLookup.value,
        color: accessPallete[500]
      });
    }

    // Value

    if (hasValue(airtime)) {
      generatedChartData.push({
        name: PAGE_TITLE.AIRTIME_AND_DATA,
        value: airtime.value,
        color: valuePallete[100]
      });
    }

    if (hasValue(transfer)) {
      generatedChartData.push({
        name: PAGE_TITLE.ACCOUNT_TRANSFERS,
        value: transfer.value,
        color: valuePallete[300]
      });
    }

    if (hasValue(onDemandPaymentAndAcquisition)) {
      generatedChartData.push({
        name: PAGE_TITLE.ONDEMAND,
        value: onDemandPaymentAndAcquisition.value,
        color: valuePallete[500]
      });
    }

    if (hasValue(payout)) {
      generatedChartData.push({
        name: PAGE_TITLE.PAYOUT,
        value: payout.value,
        color: valuePallete[700]
      });
    }

    if (hasValue(merchantPayment)) {
      generatedChartData.push({
        name: PAGE_TITLE.MERCHANT_PAYMENT,
        value: merchantPayment.value,
        color: valuePallete[900]
      });
    }

    // Identity

    if (hasValue(customerValidation)) {
      generatedChartData.push({
        name: PAGE_TITLE.CUSTOMER_VALIDATION,
        value: customerValidation.value,
        color: identityPallete[100]
      });
    }

    if (hasValue(customerAuthentication)) {
      generatedChartData.push({
        name: PAGE_TITLE.CUSTOMER_AUTHENTICATION,
        value: customerAuthentication.value,
        color: identityPallete[400]
      });
    }

    if (hasValue(customerEnrollment)) {
      generatedChartData.push({
        name: PAGE_TITLE.CUSTOMER_ENROLLMENT,
        value: customerEnrollment.value,
        color: identityPallete[700]
      });
    }
  }

  return generatedChartData;
};

export const prepareDateRangeProps = (selectedMonth: string): UrlParam[] => [
  [FILTERS.FROM, formatDateToApiFormat({ date: getBeginningOfMonth(selectedMonth) })],
  [FILTERS.TO, formatDateToApiFormat({ date: getEndOfMonth(selectedMonth), type: 'exclusive' })]
];
